import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import Terms from "@/pages/Terms";
import RichTextEditor from "@/components/RichTextEditor";

/**
 * Terms & Conditions admin — single full-document editor.
 *
 * Flow:  Paste → Edit if needed → Save draft → Preview → Publish.
 * Everything is managed as ONE complete document (no section builder,
 * no per-clause fields). Draft, preview and publish endpoints round-trip
 * through /admin/terms so the server is always the source of truth.
 */
export default function AdminTerms() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [viewingVersion, setViewingVersion] = useState(null);

  const [published, setPublished] = useState(null);
  const [title, setTitle] = useState("Terms & Conditions");
  const [contentHtml, setContentHtml] = useState("");
  const [serverDirty, setServerDirty] = useState(false);

  // Track whether the local draft differs from the last-known server draft
  // so we can warn on tab close.
  const savedSnapshot = useRef("");
  const localDirty = useMemo(
    () => JSON.stringify({ title, contentHtml }) !== savedSnapshot.current,
    [title, contentHtml]
  );

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/terms/state");
      setPublished(r.data.published);
      setTitle(r.data.draft.title || "Terms & Conditions");
      setContentHtml(r.data.draft.content_html || "");
      setServerDirty(r.data.has_unpublished_changes);
      savedSnapshot.current = JSON.stringify({
        title: r.data.draft.title || "Terms & Conditions",
        contentHtml: r.data.draft.content_html || "",
      });
    } catch {
      toast.error("Failed to load Terms editor");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  useEffect(() => {
    const h = (e) => {
      if (!localDirty) return;
      e.preventDefault(); e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [localDirty]);

  const bodyPayload = () => ({
    title: (title || "").trim() || "Terms & Conditions",
    content_html: contentHtml || "",
  });

  const saveDraft = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/terms/draft", bodyPayload());
      toast.success("Draft saved");
      const d = r.data.draft;
      setTitle(d.title || "Terms & Conditions");
      setContentHtml(d.content_html || "");
      savedSnapshot.current = JSON.stringify({
        title: d.title || "Terms & Conditions",
        contentHtml: d.content_html || "",
      });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doPublish = async () => {
    setPublishing(true);
    try {
      const r = await api.post("/admin/terms/publish", bodyPayload());
      toast.success(`Terms & Conditions published (v${r.data.version})`);
      setPublishOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const loadHistory = async () => {
    setHistoryOpen(true);
    try {
      const r = await api.get("/admin/terms/versions");
      setVersions(r.data.versions || []);
    } catch {
      toast.error("Failed to load history");
    }
  };

  const viewVersion = async (vid) => {
    try {
      const r = await api.get(`/admin/terms/versions/${vid}`);
      setViewingVersion(r.data);
    } catch {
      toast.error("Failed to load version");
    }
  };

  const restoreVersion = async (vid) => {
    if (!window.confirm(
      "Restore this version into the working draft? Your current draft will be replaced. You'll still need to click Publish to make it live."
    )) return;
    try {
      await api.post(`/admin/terms/versions/${vid}/restore`);
      toast.success("Version restored into draft");
      setHistoryOpen(false); setViewingVersion(null);
      reload();
    } catch {
      toast.error("Restore failed");
    }
  };

  const previewData = useMemo(() => ({
    published: true,
    version: published?.version ? `${published.version} (preview)` : "1.0 (preview)",
    title: title || "Terms & Conditions",
    content_html: contentHtml || "",
    published_at: null,
  }), [title, contentHtml, published]);

  return (
    <div className="space-y-6" data-testid="admin-terms-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl text-stone-900">Terms & Conditions</h2>
          <p className="text-sm text-stone-600 mt-1">
            Paste or edit your complete Terms & Conditions document below. Save a draft, preview it, then publish when ready.
          </p>
        </div>
        <button
          onClick={loadHistory}
          data-testid="admin-terms-history"
          className="mono-label px-4 py-2 border hairline text-stone-700"
        >
          Version history
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="border hairline bg-white p-5" data-testid="admin-terms-published-card">
          <p className="mono-label text-[10px] text-stone-500">CURRENT PUBLISHED VERSION</p>
          {published ? (
            <>
              <p className="font-serif text-2xl text-stone-900 mt-1">Version {published.version}</p>
              <p className="text-xs text-stone-500 mt-1">
                Last published {new Date(published.published_at).toLocaleString("en-GB", {
                  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                })}
              </p>
            </>
          ) : (
            <p className="text-sm text-amber-700 mt-2">No version has been published yet.</p>
          )}
        </div>
        <div className="border hairline bg-white p-5" data-testid="admin-terms-draft-card">
          <p className="mono-label text-[10px] text-stone-500">DRAFT STATUS</p>
          {(serverDirty || localDirty) ? (
            <p className="font-serif text-lg text-stone-900 mt-1" data-testid="admin-terms-dirty-flag">
              ● Draft changes not yet published
            </p>
          ) : (
            <p className="font-serif text-lg text-emerald-700 mt-1" data-testid="admin-terms-clean-flag">
              ✓ Published — No unpublished changes
            </p>
          )}
          <p className="text-xs text-stone-500 mt-1">
            {localDirty ? "You have unsaved local edits" : "Draft matches the server"}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-stone-500">Loading editor…</p>
      ) : (
        <>
          <div className="border hairline bg-white p-5">
            <label className="block mb-5">
              <span className="mono-label text-stone-600">Page title</span>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="admin-terms-title"
                className="mt-1 w-full border hairline px-3 py-2 text-[15px]"
              />
            </label>

            <p className="mono-label text-stone-600 mb-2">Terms & Conditions document</p>
            <RichTextEditor
              value={contentHtml}
              onChange={setContentHtml}
              placeholder="Paste or edit your complete Terms & Conditions here. Headings, bold, italics, paragraphs, numbered lists and bullet points will all be preserved exactly as entered."
            />
            <p className="text-[11px] text-stone-500 mt-2">
              Tip: paste from Word / Google Docs and the formatting (headings, bold, lists) will be preserved automatically.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap sticky bottom-0 bg-[#FAF9F6] py-4 border-t hairline z-10">
            <button
              onClick={saveDraft}
              disabled={saving || publishing}
              data-testid="admin-terms-save"
              className="mono-label px-6 py-2.5 border hairline text-stone-700 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save draft"}
            </button>
            <button
              onClick={() => setPreviewOpen(true)}
              data-testid="admin-terms-preview"
              className="mono-label px-6 py-2.5 border hairline text-stone-700"
            >
              Preview
            </button>
            <button
              onClick={() => setPublishOpen(true)}
              disabled={saving || publishing}
              data-testid="admin-terms-publish"
              className="mono-label px-6 py-2.5 bg-stone-900 text-white disabled:opacity-60"
            >
              Publish
            </button>
            <div className="ml-auto text-xs text-stone-500">
              {localDirty ? "You have unsaved local edits" : "Draft matches the server"}
            </div>
          </div>
        </>
      )}

      {/* Publish confirm */}
      {publishOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => !publishing && setPublishOpen(false)}
          data-testid="admin-terms-publish-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-md w-full border hairline p-6">
            <p className="mono-label text-stone-500">CONFIRM PUBLISH</p>
            <h3 className="font-serif text-2xl text-stone-900 mt-1">Publish Terms & Conditions?</h3>
            <p className="text-sm text-stone-700 mt-3 leading-relaxed">
              This will replace the currently published version on the CLAÈ website. Previous versions remain in your Version History.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setPublishOpen(false)}
                disabled={publishing}
                data-testid="admin-terms-publish-cancel"
                className="mono-label px-4 py-2 border hairline text-stone-700"
              >
                Cancel
              </button>
              <button
                onClick={doPublish}
                disabled={publishing}
                data-testid="admin-terms-publish-confirm"
                className="mono-label px-6 py-2 bg-stone-900 text-white disabled:opacity-60"
              >
                {publishing ? "Publishing…" : "Publish"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      {previewOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 overflow-auto p-4"
          onClick={() => setPreviewOpen(false)}
          data-testid="admin-terms-preview-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="max-w-3xl mx-auto bg-white border hairline">
            <div className="flex items-center justify-between px-5 py-3 bg-stone-900 text-white">
              <p className="mono-label text-[11px]">PREVIEW — how this draft will look publicly. Preview only — nothing is published.</p>
              <button
                onClick={() => setPreviewOpen(false)}
                data-testid="admin-terms-preview-close"
                className="mono-label text-white/80 hover:text-white"
              >
                Close ×
              </button>
            </div>
            <Terms preview={previewData} />
          </div>
        </div>
      )}

      {/* Version history drawer */}
      {historyOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => { setHistoryOpen(false); setViewingVersion(null); }}
          data-testid="admin-terms-history-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-4xl w-full border hairline max-h-[86vh] flex flex-col">
            <div className="px-5 py-4 border-b hairline flex items-center justify-between">
              <p className="font-serif text-2xl text-stone-900">Version history</p>
              <button
                onClick={() => { setHistoryOpen(false); setViewingVersion(null); }}
                data-testid="admin-terms-history-close"
                className="mono-label text-stone-500 hover:text-stone-900"
              >
                Close ×
              </button>
            </div>
            <div className="grid md:grid-cols-2 flex-1 overflow-hidden">
              <div className="overflow-auto border-r hairline">
                {versions.length === 0 && (
                  <p className="p-5 text-sm text-stone-500 italic">No versions yet.</p>
                )}
                {versions.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => viewVersion(v.id)}
                    data-testid={`admin-terms-history-row-${v.id}`}
                    className={`w-full text-left px-5 py-4 border-b hairline hover:bg-stone-50 ${
                      viewingVersion?.id === v.id ? "bg-stone-50" : ""
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <p className="font-serif text-lg text-stone-900">Version {v.version}</p>
                      <span className={`mono-label text-[10px] ${v.is_published ? "text-emerald-700" : "text-stone-500"}`}>
                        {v.is_published ? "Current" : "Previous"}
                      </span>
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Published {new Date(v.published_at).toLocaleDateString("en-GB", {
                        day: "2-digit", month: "long", year: "numeric",
                      })}
                    </p>
                  </button>
                ))}
              </div>
              <div className="overflow-auto p-5">
                {!viewingVersion && (
                  <p className="text-sm text-stone-500 italic">Select a version to preview it here.</p>
                )}
                {viewingVersion && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="mono-label text-[10px] text-stone-500">VERSION {viewingVersion.version}</p>
                        <p className="font-serif text-xl text-stone-900">{viewingVersion.title}</p>
                      </div>
                      <button
                        onClick={() => restoreVersion(viewingVersion.id)}
                        data-testid="admin-terms-history-restore"
                        className="mono-label px-3 py-1.5 border hairline text-stone-700"
                      >
                        Restore into draft
                      </button>
                    </div>
                    <div
                      className="clae-terms-content text-[13px] leading-[1.8] text-stone-800"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewingVersion.content_html || "") }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
